import * as sentry from '@sentry/react';
import {LatLng} from 'use-places-autocomplete';

import {config} from 'config/env';

import {TPinMapSelectedLocation, DistanceResult} from '../types';

// Extract specific address component from Google response
export const extractGoogleMapComponent = (components: google.maps.GeocoderAddressComponent[], type: string) => {
  const component = components?.find((c) => c.types.includes(type));
  return component ? component.long_name : '';
};

export const extractGoogleAddressComponents = (
  placeDetails: google.maps.places.PlaceResult | google.maps.GeocoderResult,
  placeTitle = ''
): TPinMapSelectedLocation | null => {
  const addressComponents = placeDetails?.address_components;

  if (addressComponents) {
    const postalCode = extractGoogleMapComponent(addressComponents, 'postal_code');
    const province = extractGoogleMapComponent(addressComponents, 'administrative_area_level_1');
    let district = extractGoogleMapComponent(addressComponents, 'administrative_area_level_2');
    if (!district) {
      district = extractGoogleMapComponent(addressComponents, 'sublocality_level_1');
    }
    const subdistrict =
      extractGoogleMapComponent(addressComponents, 'sublocality') ||
      extractGoogleMapComponent(addressComponents, 'locality');
    const coordinates = placeDetails.geometry?.location?.toJSON() as LatLng;

    const excludeTypes = [
      'postal_code',
      'administrative_area_level_1',
      'administrative_area_level_2',
      'sublocality',
      'country'
    ];

    const address = addressComponents
      .filter((component) => !component.types.some((type) => excludeTypes.includes(type)))
      .map((component) => component.long_name)
      .join(', ');

    return {
      postalCode,
      province,
      district,
      subdistrict,
      coordinates,
      address: placeTitle ? `${placeTitle}, ${address}` : address
    };
  }
  return null;
};

const isGoogleMapsLoaded = (): boolean => {
  return typeof window !== 'undefined' && !!window.google?.maps;
};

export const geocodeAddress = (address: string, originalTargetZipcode: string): Promise<google.maps.LatLng | null> => {
  return new Promise((resolve, reject) => {
    if (!isGoogleMapsLoaded()) {
      return reject('Google Maps API not loaded.');
    }

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({address}, (results: any, status: any) => {
      if (status === 'OK' && results.length) {
        const matchedByZipcode = results.find((record: any) => {
          const addressComponents = record.address_components;
          const zipCodeAddressComponent = addressComponents.find((component: any) =>
            component.types.includes('postal_code')
          );
          return (
            zipCodeAddressComponent &&
            (zipCodeAddressComponent.long_name === originalTargetZipcode ||
              zipCodeAddressComponent.short_name === originalTargetZipcode)
          );
        });
        resolve(matchedByZipcode?.geometry.location || null);
      } else if (status === 'ZERO_RESULTS' && !results) {
        resolve(null);
      } else {
        reject(`Geocoding failed for "${address}" (${status})`);
      }
    });
  });
};

// const getDrivingDistance = (origin: LatLng, destination: LatLng): Promise<number> => {
//   return new Promise((resolve, reject) => {
//     if (!isGoogleMapsLoaded()) {
//       return reject('Google Maps API not loaded.');
//     }
//     if (!window.google?.maps?.DirectionsService) {
//       return reject('Direction service library not available.');
//     }
//     const directionsService = new window.google.maps.DirectionsService();
//     directionsService.route(
//       {
//         origin: new window.google.maps.LatLng(origin),
//         destination: new window.google.maps.LatLng(destination),
//         travelMode: window.google.maps.TravelMode.DRIVING
//       },
//       (result: any, status: any) => {
//         if (status === 'OK' && result?.routes.length && result.routes[0]?.legs.length) {
//           const leg = result.routes[0].legs[0];
//           resolve(leg.distance.value / 1000);
//         } else {
//           reject(`Directions request failed: ${status}`);
//         }
//       }
//     );
//   });
// };

export const getSphericalDistance = async (origin: LatLng, destination: LatLng): Promise<number> => {
  if (!isGoogleMapsLoaded()) {
    throw new Error('Google Maps API not loaded.');
  }
  if (!window.google.maps.geometry?.spherical) {
    throw new Error('Spherical geometry library not available.');
  }

  const originLatLng = new window.google.maps.LatLng(origin);
  const destinationLatLng = new window.google.maps.LatLng(destination);
  const sphericalDistanceMeters = window.google.maps.geometry.spherical.computeDistanceBetween(
    originLatLng,
    destinationLatLng
  );

  return sphericalDistanceMeters / 1000;
};

export const getDistanceFromCoordsToAddress = async (
  origin: LatLng,
  destinationAddress: string,
  originalTargetZipcode: string
): Promise<DistanceResult> => {
  if (!isGoogleMapsLoaded()) {
    throw new Error('Google Maps API not loaded.');
  }

  const destinationCoords = await geocodeAddress(destinationAddress, originalTargetZipcode);

  let destination;
  if (!destinationCoords) {
    destination = {lat: 0, lng: 0};
    sentry.captureMessage(`No geocoding results found for address: ${destinationAddress}`);
  } else {
    destination = {lat: destinationCoords.lat(), lng: destinationCoords.lng()};
  }

  const sphericalDistanceInKm = await getSphericalDistance(origin, destination);
  const roundedDistanceKm = Math.round(sphericalDistanceInKm * 100) / 100;
  return {
    distanceKm: roundedDistanceKm,
    latLng: destination,
    addressString: destinationAddress,
    isDistanceBeyondThreshold: sphericalDistanceInKm > config.PIN_DISTANCE_THRESHOLD
  };
};

export const loadGoogleMapsApi = (apiKey: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve();
      return;
    }

    const existingScript = document.getElementById('googleMaps');

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve());
      return;
    }

    const script = document.createElement('script');
    script.id = 'googleMaps';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
};
